import React, { useEffect, useState, useContext } from "react";
import { getApiBaseURL } from "../../utils/api";
import secureAxios from "../../utils/secureAxios";
import { AuthContext } from "../../contexts/AuthContext";

const RoleList = ({ onEdit, refreshTrigger }) => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const apiBase = getApiBaseURL();
  const { user } = useContext(AuthContext);

  useEffect(() => {
    fetchRoles();
  }, [refreshTrigger]);

  const fetchRoles = async () => {
    setLoading(true);
    setError(null);

    try {
      // HIPAA Compliant: Using secureAxios with httpOnly cookies
      const response = await secureAxios.get(`${apiBase}/api/roles`);
      setRoles(response.data);
    } catch (err) {
      console.error("❌ Error loading roles:", err);
      setError(err.response?.data?.error || "Failed to load roles");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (roleId, roleName) => {
    // Prevent deletion of critical roles
    if (["admin", "super_admin"].includes(roleName.toLowerCase())) {
      alert("❌ Cannot delete system-critical roles");
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to delete the role "${roleName}"?\n\nThis action cannot be undone.`
      )
    ) {
      return;
    }

    try {
      await secureAxios.delete(`${apiBase}/api/roles/${roleId}`);
      setRoles((prev) => prev.filter((r) => r.role_id !== roleId));
      alert("✅ Role deleted successfully!");
    } catch (err) {
      console.error("❌ Failed to delete role:", err);
      alert(err.response?.data?.error || "Failed to delete role");
    }
  };

  if (loading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
        <button
          className="btn btn-sm btn-outline-danger ms-3"
          onClick={fetchRoles}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4>Role Management</h4>
        <span className="badge bg-secondary">{roles.length} roles</span>
      </div>

      {/* Desktop & Tablet View */}
      <div className="table-responsive d-none d-md-block">
        <table className="table table-hover">
          <thead className="table-light">
            <tr>
              <th scope="col">Role</th>
              <th scope="col">Description</th>
              <th scope="col">Last Updated</th>
              <th scope="col" className="text-end">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {roles.length === 0 ? (
              <tr>
                <td colSpan="4" className="text-center text-muted">
                  No roles found.
                </td>
              </tr>
            ) : (
              roles.map((r) => (
                <tr key={r.role_id}>
                  <td>
                    <strong>{r.role}</strong>
                    {["admin", "super_admin"].includes(
                      r.role.toLowerCase()
                    ) && (
                      <span className="badge bg-warning text-dark ms-2">
                        System
                      </span>
                    )}
                  </td>
                  <td>
                    {r.role_description || (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td>
                    <small className="text-muted">
                      {r.updated_at
                        ? new Date(r.updated_at).toLocaleDateString()
                        : "—"}
                    </small>
                  </td>
                  <td className="text-end">
                    {user?.role === "admin" ? (
                      <>
                        <button
                          className="btn btn-sm btn-outline-primary me-2"
                          onClick={() => onEdit(r)}
                          title="Edit role"
                        >
                          <i className="bi bi-pencil"></i> Edit
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(r.role_id, r.role)}
                          title="Delete role"
                          disabled={["admin", "super_admin"].includes(
                            r.role.toLowerCase()
                          )}
                        >
                          <i className="bi bi-trash"></i> Delete
                        </button>
                      </>
                    ) : (
                      <span className="text-muted">View only</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile View */}
      <div className="d-block d-md-none">
        {roles.length === 0 ? (
          <div className="alert alert-info">No roles found.</div>
        ) : (
          roles.map((r) => (
            <div key={r.role_id} className="card mb-3">
              <div className="card-body">
                <h5 className="card-title">
                  {r.role}
                  {["admin", "super_admin"].includes(r.role.toLowerCase()) && (
                    <span className="badge bg-warning text-dark ms-2">
                      System
                    </span>
                  )}
                </h5>
                <p className="card-text">
                  {r.role_description || (
                    <span className="text-muted">No description</span>
                  )}
                </p>
                <p className="card-text">
                  <small className="text-muted">
                    Last updated:{" "}
                    {r.updated_at
                      ? new Date(r.updated_at).toLocaleDateString()
                      : "N/A"}
                  </small>
                </p>
                {user?.role === "admin" && (
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => onEdit(r)}
                    >
                      <i className="bi bi-pencil"></i> Edit
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDelete(r.role_id, r.role)}
                      disabled={["admin", "super_admin"].includes(
                        r.role.toLowerCase()
                      )}
                    >
                      <i className="bi bi-trash"></i> Delete
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* HIPAA Compliance Notice */}
      <div className="mt-3">
        <small className="text-muted">
          <i className="bi bi-shield-lock"></i> Role changes affect system
          access control and are logged for security audit.
        </small>
      </div>
    </div>
  );
};

export default RoleList;
